import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, DatePicker, Switch, Button, message, Space } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { customBaseUrl, httpPut } from '../../../store/http';
import { hideLoader, showLoader } from '../../../utils/loader';
import Swal from 'sweetalert2';

const { Option } = Select;
const { TextArea } = Input;

const EditPricingWithCommissionModal = ({ showModal, hideModal, reloadPricingPlans, productCode, pricingPlan }) => {
  const [form] = Form.useForm();
  const [pricingModel, setPricingModel] = useState('FIXED');

  useEffect(() => {
    if (pricingPlan) {
      form.setFieldsValue({
        ...pricingPlan,
        effectiveFrom: pricingPlan.effectiveFrom ? moment(pricingPlan.effectiveFrom) : null,
        effectiveTo: pricingPlan.effectiveTo ? moment(pricingPlan.effectiveTo) : null,
        commissionEffectiveFrom: pricingPlan.commissionEffectiveFrom ? moment(pricingPlan.commissionEffectiveFrom) : null,
        commissionEffectiveTo: pricingPlan.commissionEffectiveTo ? moment(pricingPlan.commissionEffectiveTo) : null,
      });
      setPricingModel(pricingPlan.pricingModel);
    }
  }, [pricingPlan, form]);

  const onFinish = async (values) => {
    showLoader();
    try {
      const payload = {
        ...values,
        productCode: productCode,
        effectiveFrom: values.effectiveFrom ? values.effectiveFrom.toISOString() : undefined,
        effectiveTo: values.effectiveTo ? values.effectiveTo.toISOString() : undefined,
        commissionEffectiveFrom: values.commissionEffectiveFrom ? values.commissionEffectiveFrom.toISOString() : undefined,
        commissionEffectiveTo: values.commissionEffectiveTo ? values.commissionEffectiveTo.toISOString() : undefined,
        isActive: values.isActive !== undefined ? values.isActive : true,
        commissionActive: values.commissionActive !== undefined ? values.commissionActive : true,
        pricingTiers: pricingModel !== 'FIXED' ? values.pricingTiers?.map(tier => ({
          ...tier,
          isActive: tier.isActive !== undefined ? tier.isActive : true,
        })) : undefined,
        pricingRules: pricingModel === 'VOLUME_BASED' ? values.pricingRules?.map(rule => ({
          ...rule,
          effectiveFrom: rule.effectiveFrom ? rule.effectiveFrom.toISOString() : undefined,
          effectiveTo: rule.effectiveTo ? rule.effectiveTo.toISOString() : undefined,
          isActive: rule.isActive !== undefined ? rule.isActive : true,
        })) : undefined,
      };

      const response = await httpPut(`${customBaseUrl.integUrl}/api/v1/pricing/products/pricing-with-commission/${pricingPlan.pricingId}`, payload);
      if (response.status) {
        message.success('Pricing plan with commission updated successfully');
        hideModal();
        reloadPricingPlans();
      } else {
        Swal.fire('Error', response.message || 'Failed to update pricing plan with commission', 'error');
      }
    } catch (error) {
      console.error('Error updating pricing plan with commission:', error);
      Swal.fire('Error', 'An error occurred while updating pricing plan with commission', 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <Modal
      title={`Edit Pricing Plan with Commission for ${productCode}`}
      open={showModal}
      onCancel={hideModal}
      footer={null}
      width={1200}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        {/* Basic Pricing Plan Fields */}
        <Form.Item
          name="pricingName"
          label="Pricing Plan Name"
          rules={[{ required: true, message: 'Please enter pricing plan name' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="pricingDescription"
          label="Description"
          rules={[{ required: true, message: 'Please enter pricing plan description' }]}
        >
          <TextArea rows={2} />
        </Form.Item>
        <Form.Item
          name="pricingModel"
          label="Pricing Model"
          rules={[{ required: true, message: 'Please select a pricing model' }]}
        >
          <Select onChange={setPricingModel}>
            <Option value="FIXED">FIXED</Option>
            <Option value="TIERED">TIERED</Option>
            <Option value="VOLUME_BASED">VOLUME_BASED</Option>
          </Select>
        </Form.Item>

        {pricingModel === 'FIXED' && (
          <>
            <Form.Item
              name="basePrice"
              label="Base Price"
              rules={[{ required: true, message: 'Please enter base price' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="currency"
              label="Currency"
              rules={[{ required: true, message: 'Please select currency' }]}
            >
              <Select>
                <Option value="NGN">NGN</Option>
                <Option value="USD">USD</Option>
              </Select>
            </Form.Item>
          </>
        )}

        <Form.Item
          name="effectiveFrom"
          label="Effective From"
          rules={[{ required: true, message: 'Please select effective from date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="effectiveTo"
          label="Effective To"
          rules={[{ required: true, message: 'Please select effective to date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="isActive" label="Is Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true, message: 'Please enter priority' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        {/* Pricing Tiers */}
        {(pricingModel === 'TIERED' || pricingModel === 'VOLUME_BASED') && (
          <>
            <h3>Pricing Tiers</h3>
            <Form.List name="pricingTiers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'tierName']}
                        rules={[{ required: true, message: 'Missing tier name' }]}
                      >
                        <Input placeholder="Tier Name" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'tierDescription']}
                      >
                        <Input placeholder="Tier Description" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'minAmount']}
                        rules={[{ required: true, message: 'Missing min amount' }]}
                      >
                        <InputNumber placeholder="Min Amount" min={0} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'maxAmount']}
                        rules={[{ required: true, message: 'Missing max amount' }]}
                      >
                        <InputNumber placeholder="Max Amount" min={0} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'unitPrice']}
                      >
                        <InputNumber placeholder="Unit Price" min={0} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'fixedFee']}
                      >
                        <InputNumber placeholder="Fixed Fee" min={0} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'percentageRate']}
                      >
                        <InputNumber placeholder="Percentage Rate" min={0} max={1} step={0.01} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'discountPercentage']}
                      >
                        <InputNumber placeholder="Discount %" min={0} max={100} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'priority']}
                        rules={[{ required: true, message: 'Missing priority' }]}
                      >
                        <InputNumber placeholder="Priority" min={1} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'isActive']}
                        valuePropName="checked"
                        initialValue={true}
                      >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Tier
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        {/* Pricing Rules */}
        {pricingModel === 'VOLUME_BASED' && (
          <>
            <h3>Pricing Rules</h3>
            <Form.List name="pricingRules">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'ruleName']}
                        rules={[{ required: true, message: 'Missing rule name' }]}
                      >
                        <Input placeholder="Rule Name" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'ruleDescription']}
                      >
                        <Input placeholder="Rule Description" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'ruleType']}
                        rules={[{ required: true, message: 'Missing rule type' }]}
                      >
                        <Select placeholder="Rule Type">
                          <Option value="VOLUME_BASED">VOLUME_BASED</Option>
                          <Option value="TIME_BASED">TIME_BASED</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'conditionField']}
                      >
                        <Input placeholder="Condition Field" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'conditionOperator']}
                      >
                        <Select placeholder="Condition Operator">
                          <Option value="GREATER_THAN">GREATER_THAN</Option>
                          <Option value="LESS_THAN">LESS_THAN</Option>
                          <Option value="EQUALS">EQUALS</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'conditionValue']}
                      >
                        <InputNumber placeholder="Condition Value" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'actionType']}
                      >
                        <Select placeholder="Action Type">
                          <Option value="DISCOUNT_PERCENTAGE">DISCOUNT_PERCENTAGE</Option>
                          <Option value="FIXED_DISCOUNT">FIXED_DISCOUNT</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'actionValue']}
                      >
                        <InputNumber placeholder="Action Value" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'effectiveFrom']}
                      >
                        <DatePicker showTime placeholder="Rule Effective From" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'effectiveTo']}
                      >
                        <DatePicker showTime placeholder="Rule Effective To" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'priority']}
                        rules={[{ required: true, message: 'Missing priority' }]}
                      >
                        <InputNumber placeholder="Priority" min={1} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'isActive']}
                        valuePropName="checked"
                        initialValue={true}
                      >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Rule
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}

        <h3 style={{ marginTop: 20 }}>Commission Configuration</h3>
        <Form.Item name="commissionActive" label="Commission Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="baseCommissionRate"
          label="Base Commission Rate"
          rules={[{ required: true, message: 'Please enter base commission rate' }]}
        >
          <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="commissionAccountName"
          label="Commission Account Name"
          rules={[{ required: true, message: 'Please enter commission account name' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="commissionAccountNumber"
          label="Commission Account Number"
          rules={[{ required: true, message: 'Please enter commission account number' }]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="commissionBankCode"
          label="Commission Bank Code"
          rules={[{ required: true, message: 'Please enter commission bank code' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="commissionEffectiveFrom"
          label="Commission Effective From"
          rules={[{ required: true, message: 'Please select commission effective from date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="commissionEffectiveTo"
          label="Commission Effective To"
          rules={[{ required: true, message: 'Please select commission effective to date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="commissionType"
          label="Commission Type"
          rules={[{ required: true, message: 'Please select commission type' }]}
        >
          <Select>
            <Option value="PERCENTAGE">PERCENTAGE</Option>
            <Option value="FIXED">FIXED</Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="maximumAmount"
          label="Maximum Amount"
          rules={[{ required: true, message: 'Please enter maximum amount' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="minimumAmount"
          label="Minimum Amount"
          rules={[{ required: true, message: 'Please enter minimum amount' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Update Pricing Plan with Commission
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPricingWithCommissionModal;